import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, MicOff, Play, Pause, RotateCcw, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface VoiceNoteRecorderProps {
  gameSessionId: string;
  moveNumber: number;
  onSave?: (voiceNoteId: string) => void;
  onCancel?: () => void;
  prompt?: string;
  className?: string;
}

export const VoiceNoteRecorder: React.FC<VoiceNoteRecorderProps> = ({
  gameSessionId,
  moveNumber,
  onSave,
  onCancel,
  prompt = "What were you thinking?",
  className = ""
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startTimeRef = useRef<number>(0);
  const { user } = useAuth();
  const { toast } = useToast();

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      const chunks: Blob[] = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm;codecs=opus' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setDuration(Date.now() - startTimeRef.current);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      };
      
      mediaRecorderRef.current = mediaRecorder;
      startTimeRef.current = Date.now();
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: "Recording Error",
        description: "Could not access microphone. Please check permissions.",
        variant: "destructive"
      });
    }
  }, [toast]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const playRecording = useCallback(() => {
    if (audioUrl) {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          audioRef.current.play();
          setIsPlaying(true);
        }
      } else {
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        audio.onended = () => setIsPlaying(false);
        audio.play();
        setIsPlaying(true);
      }
    }
  }, [audioUrl, isPlaying]);

  const resetRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration(0);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current = null;
    }
  }, [audioUrl]);

  const saveVoiceNote = useCallback(async () => {
    if (!audioBlob || !user) return;

    setIsUploading(true);
    try {
      // Upload audio file
      const fileName = `${user.id}/${gameSessionId}/${moveNumber}-${Date.now()}.webm`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('voice-notes')
        .upload(fileName, audioBlob, {
          contentType: 'audio/webm'
        });

      if (uploadError) throw uploadError;

      // Save voice note record
      const { data: voiceNote, error: dbError } = await supabase
        .from('move_voice_notes')
        .insert({
          game_session_id: gameSessionId,
          move_number: moveNumber,
          player_id: user.id,
          audio_file_path: uploadData.path,
          duration_ms: duration
        })
        .select()
        .single();

      if (dbError) throw dbError;

      toast({
        title: "Voice Note Saved",
        description: "Your thought has been recorded!"
      });

      onSave?.(voiceNote.id);
    } catch (error) {
      console.error('Error saving voice note:', error);
      toast({
        title: "Save Error",
        description: "Could not save your voice note. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  }, [audioBlob, user, gameSessionId, moveNumber, duration, onSave, toast]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  if (!user) return null;

  return (
    <Card className={`voice-note-recorder ${className}`}>
      <CardContent className="p-4">
        <div className="flex flex-col items-center space-y-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-foreground">{prompt}</h3>
            <p className="text-sm text-muted-foreground">Record your thoughts about this move</p>
          </div>

          <div className="flex items-center space-x-3">
            {!audioBlob ? (
              <Button
                onClick={isRecording ? stopRecording : startRecording}
                size="lg"
                className={`${isRecording ? 'bg-destructive hover:bg-destructive/90' : 'bg-primary hover:bg-primary/90'} 
                          transition-all duration-200 ${isRecording ? 'animate-pulse' : ''}`}
              >
                {isRecording ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                {isRecording ? 'Stop Recording' : 'Start Recording'}
              </Button>
            ) : (
              <>
                <Button
                  onClick={playRecording}
                  variant="outline"
                  size="lg"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  {isPlaying ? 'Pause' : 'Play'}
                </Button>
                
                <Button
                  onClick={resetRecording}
                  variant="outline"
                  size="lg"
                >
                  <RotateCcw className="w-5 h-5" />
                  Re-record
                </Button>
              </>
            )}
          </div>

          {audioBlob && (
            <div className="flex items-center space-x-2">
              <Button
                onClick={saveVoiceNote}
                disabled={isUploading}
                className="bg-success hover:bg-success/90"
              >
                <Check className="w-4 h-4 mr-2" />
                {isUploading ? 'Saving...' : 'Save Note'}
              </Button>
              
              <Button
                onClick={onCancel}
                variant="outline"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          )}

          {duration > 0 && (
            <p className="text-sm text-muted-foreground">
              Duration: {Math.round(duration / 1000)}s
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};